import React, {
  useEffect,
  useMemo,
  useState
} from 'react';

import certificateApi
  from '../services/certificateApi';


const CertificateManagement =
  () => {

  const [templates, setTemplates] =
    useState([]);

  const [batches, setBatches] =
    useState([]);

  const [batchId, setBatchId] =
    useState('');

  const [batch, setBatch] =
    useState(null);

  const [
    certificates,
    setCertificates
  ] = useState([]);

  const [outputs, setOutputs] =
    useState([]);

  const [selectedIds, setSelectedIds] =
    useState([]);

  const [loading, setLoading] =
    useState(false);

  const [message, setMessage] =
    useState('');

  const [excelFile, setExcelFile] =
    useState(null);

  const [templateId, setTemplateId] =
    useState('');

  const [batchName, setBatchName] =
    useState('');

  const [common, setCommon] =
    useState({
      programme_name: '',
      sponsor_name: '',
      from_date: '',
      to_date: '',
      issued_on: '',
      issued_by:
        'SIRD&PR, Tamil Nadu'
    });

  const [search, setSearch] =
    useState('');

  const [groupFilter, setGroupFilter] =
    useState('all');

  const [
    generationFilter,
    setGenerationFilter
  ] = useState('all');

  const [
    emailFilter,
    setEmailFilter
  ] = useState('all');

  const [
    rowFilter,
    setRowFilter
  ] = useState('all');

  const [pdfMode, setPdfMode] =
    useState('INDIVIDUAL');

  const [emailMode, setEmailMode] =
    useState(
      'INDIVIDUAL_EMAIL'
    );

  const [
    commonRecipient,
    setCommonRecipient
  ] = useState('');

  const [subject, setSubject] =
    useState(
      'Training Certificate'
    );

  const [emailBody, setEmailBody] =
    useState(
      'Please find the certificate attached.'
    );

  const [
    generatedOutputIds,
    setGeneratedOutputIds
  ] = useState([]);


  const loadInitial =
    async () => {
      try {
        const [
          templateResult,
          batchResult
        ] =
          await Promise.all([
            certificateApi
              .getTemplates(),

            certificateApi
              .getBatches()
          ]);

        setTemplates(
          templateResult.data ||
          []
        );

        setBatches(
          batchResult.data ||
          []
        );

      } catch (error) {
        setMessage(
          error.message
        );
      }
    };


  useEffect(() => {
    loadInitial();
  }, []);


  const loadBatch =
    async (id) => {
      if (!id) return;

      try {
        setLoading(true);

        const result =
          await certificateApi
            .getBatch(id);

        setBatch(
          result.data.batch
        );

        setCertificates(
          result.data
            .certificates ||
          []
        );

        setOutputs(
          result.data.outputs ||
          []
        );

        setSelectedIds([]);

      } catch (error) {
        setMessage(
          error.message
        );

      } finally {
        setLoading(false);
      }
    };


  const handleBatchChange =
    async (event) => {
      const value =
        event.target.value;

      setBatchId(value);

      await loadBatch(value);
    };


  const handleImport =
    async () => {
      if (
        !excelFile ||
        !templateId ||
        !batchName.trim()
      ) {
        setMessage(
          'Template, Batch Name and Excel file are required'
        );

        return;
      }

      try {
        setLoading(true);

        const result =
          await certificateApi
            .importBatch({
              file:
                excelFile,

              templateId,

              batchName,

              commonFields:
                common
            });

        setMessage(
          `Upload completed. Valid: ${result.valid}, Invalid: ${result.invalid}`
        );

        await loadInitial();

        setBatchId(
          String(
            result.batch_id
          )
        );

        await loadBatch(
          result.batch_id
        );

      } catch (error) {
        setMessage(
          error.message
        );

      } finally {
        setLoading(false);
      }
    };


  const groups =
    useMemo(() => {
      return [
        ...new Set(
          certificates
            .map(
              item =>
                item.group_name
            )
            .filter(Boolean)
        )
      ].sort();

    }, [certificates]);


  const filtered =
    useMemo(() => {
      const text =
        search
          .trim()
          .toLowerCase();

      return certificates.filter(
        item => {

          if (
            groupFilter !==
              'all' &&
            item.group_name !==
              groupFilter
          ) {
            return false;
          }

          if (
            generationFilter !==
              'all' &&
            item
              .generation_status !==
              generationFilter
          ) {
            return false;
          }

          if (
            emailFilter !==
              'all' &&
            item.email_status !==
              emailFilter
          ) {
            return false;
          }

          if (
            rowFilter !==
              'all' &&
            item.row_status !==
              rowFilter
          ) {
            return false;
          }

          if (text) {
            const haystack = [
              item
                .certificate_no,

              item
                .participant_name,

              item
                .group_name,

              item
                .recipient_email
            ]
              .join(' ')
              .toLowerCase();

            if (
              !haystack.includes(
                text
              )
            ) {
              return false;
            }
          }

          return true;
        }
      );

    }, [
      certificates,
      search,
      groupFilter,
      generationFilter,
      emailFilter,
      rowFilter
    ]);


  const validVisible =
    filtered.filter(
      item =>
        item.row_status ===
        'Valid'
    );


  const selectAllVisible =
    () => {
      const visibleIds =
        validVisible.map(
          item => item.id
        );

      setSelectedIds(
        previous => [
          ...new Set([
            ...previous,
            ...visibleIds
          ])
        ]
      );
    };


  const clearSelection =
    () => {
      setSelectedIds([]);
    };


  const toggleSelection =
    (id) => {
      setSelectedIds(
        previous =>
          previous.includes(id)
            ? previous.filter(
                item =>
                  item !== id
              )
            : [
                ...previous,
                id
              ]
      );
    };


  const generate =
    async () => {

      if (
        pdfMode !== 'BATCH' &&
        selectedIds.length === 0
      ) {
        setMessage(
          'Select at least one participant'
        );

        return;
      }

      if (
        !window.confirm(
          `Generate certificates?\n\nPDF Mode: ${pdfMode}\nSelected: ${selectedIds.length}`
        )
      ) {
        return;
      }

      try {
        setLoading(true);

        const result =
          await certificateApi
            .generate({
              batchId,
              certificateIds:
                selectedIds,
              pdfMode
            });

        setGeneratedOutputIds(
          result.output_ids ||
          []
        );

        setMessage(
          `Generated: ${result.generated}, Failed: ${result.failed}`
        );

        await loadBatch(
          batchId
        );

      } catch (error) {
        setMessage(
          error.message
        );

      } finally {
        setLoading(false);
      }
    };


  const sendEmail =
    async () => {

      if (
        selectedIds.length === 0 &&
        emailMode !==
          'GROUP_EMAIL'
      ) {
        setMessage(
          'Select participants first'
        );

        return;
      }

      let outputIds =
        generatedOutputIds;

      if (
        emailMode ===
          'GROUP_EMAIL' &&
        outputIds.length === 0
      ) {
        outputIds =
          outputs
            .filter(
              item =>
                item.output_type ===
                'GROUP'
            )
            .map(
              item =>
                item.id
            );
      }

      if (
        emailMode ===
          'ONE_EMAIL_COMBINED' &&
        outputIds.length !== 1
      ) {
        setMessage(
          'Generate/select one combined PDF first'
        );

        return;
      }

      if (
        !window.confirm(
          'Send certificate email now?'
        )
      ) {
        return;
      }

      try {
        setLoading(true);

        const result =
          await certificateApi
            .sendEmail({
              batch_id:
                Number(
                  batchId
                ),

              send_mode:
                emailMode,

              certificate_ids:
                selectedIds,

              output_ids:
                outputIds,

              recipient_email:
                commonRecipient,

              subject,

              body:
                emailBody
            });

        setMessage(
          `Email completed. Sent: ${result.sent}, Failed: ${result.failed}`
        );

        await loadBatch(
          batchId
        );

      } catch (error) {
        setMessage(
          error.message
        );

      } finally {
        setLoading(false);
      }
    };


  const summary =
    useMemo(() => ({
      total:
        certificates.length,

      valid:
        certificates.filter(
          x =>
            x.row_status ===
            'Valid'
        ).length,

      invalid:
        certificates.filter(
          x =>
            x.row_status ===
            'Invalid'
        ).length,

      generated:
        certificates.filter(
          x =>
            x.generation_status ===
            'Generated'
        ).length,

      sent:
        certificates.filter(
          x =>
            x.email_status ===
            'Sent'
        ).length,

      failed:
        certificates.filter(
          x =>
            x.email_status ===
            'Failed'
        ).length

    }), [certificates]);


  return (
    <div className="p-4 sm:p-6 space-y-6">

      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Certificate Management
        </h1>

        <p className="text-sm text-gray-500 mt-1">
          Bulk certificate generation and email
        </p>
      </div>


      {message && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded text-blue-800">
          {message}
        </div>
      )}


      <div className="bg-white rounded-lg shadow p-4">

        <h2 className="font-semibold mb-4">
          Create Certificate Batch
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

          <select
            value={templateId}
            onChange={
              e =>
                setTemplateId(
                  e.target.value
                )
            }
            className="border rounded px-3 py-2"
          >
            <option value="">
              Select Template
            </option>

            {templates.map(
              item => (
                <option
                  key={item.id}
                  value={item.id}
                >
                  {item.template_name}
                </option>
              )
            )}
          </select>


          <input
            value={batchName}
            onChange={
              e =>
                setBatchName(
                  e.target.value
                )
            }
            placeholder="Batch Name"
            className="border rounded px-3 py-2"
          />


          <button
            onClick={
              certificateApi
                .downloadSample
            }
            className="bg-gray-700 text-white rounded px-4 py-2"
          >
            Download Sample Excel
          </button>


          <input
            value={
              common.programme_name
            }
            onChange={
              e =>
                setCommon({
                  ...common,
                  programme_name:
                    e.target.value
                })
            }
            placeholder="Programme Name"
            className="border rounded px-3 py-2"
          />


          <input
            value={
              common.sponsor_name
            }
            onChange={
              e =>
                setCommon({
                  ...common,
                  sponsor_name:
                    e.target.value
                })
            }
            placeholder="Sponsor / College"
            className="border rounded px-3 py-2"
          />


          <input
            value={
              common.issued_by
            }
            onChange={
              e =>
                setCommon({
                  ...common,
                  issued_by:
                    e.target.value
                })
            }
            placeholder="Issued By"
            className="border rounded px-3 py-2"
          />


          <div>
            <label className="text-xs">
              From Date
            </label>

            <input
              type="date"
              value={
                common.from_date
              }
              onChange={
                e =>
                  setCommon({
                    ...common,
                    from_date:
                      e.target.value
                  })
              }
              className="border rounded px-3 py-2 w-full"
            />
          </div>


          <div>
            <label className="text-xs">
              To Date
            </label>

            <input
              type="date"
              value={
                common.to_date
              }
              onChange={
                e =>
                  setCommon({
                    ...common,
                    to_date:
                      e.target.value
                  })
              }
              className="border rounded px-3 py-2 w-full"
            />
          </div>


          <div>
            <label className="text-xs">
              Issued On
            </label>

            <input
              type="date"
              value={
                common.issued_on
              }
              onChange={
                e =>
                  setCommon({
                    ...common,
                    issued_on:
                      e.target.value
                  })
              }
              className="border rounded px-3 py-2 w-full"
            />
          </div>


          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={
              e =>
                setExcelFile(
                  e.target.files?.[0] ||
                  null
                )
            }
            className="border rounded px-3 py-2 md:col-span-2"
          />


          <button
            onClick={handleImport}
            disabled={loading}
            className="bg-blue-600 text-white rounded px-4 py-2"
          >
            Upload & Validate Excel
          </button>

        </div>
      </div>


      <div className="bg-white rounded-lg shadow p-4">

        <label className="block text-sm font-medium mb-2">
          Existing Batch
        </label>

        <select
          value={batchId}
          onChange={
            handleBatchChange
          }
          className="border rounded px-3 py-2 w-full md:w-96"
        >
          <option value="">
            Select Batch
          </option>

          {batches.map(
            item => (
              <option
                key={item.id}
                value={item.id}
              >
                #{item.id} - {item.batch_name}
              </option>
            )
          )}
        </select>

      </div>


      {batch && (
        <>

          <div className="grid grid-cols-2 md:grid-cols-6 gap-3">

            {[
              ['Total', summary.total],
              ['Valid', summary.valid],
              ['Invalid', summary.invalid],
              ['Generated', summary.generated],
              ['Sent', summary.sent],
              ['Failed', summary.failed]
            ].map(
              ([label, value]) => (

                <div
                  key={label}
                  className="bg-white border rounded-lg p-4 text-center"
                >
                  <div className="text-sm text-gray-500">
                    {label}
                  </div>

                  <div className="text-2xl font-bold">
                    {value}
                  </div>
                </div>

              )
            )}

          </div>


          <div className="bg-white rounded-lg shadow p-4">

            <div className="grid grid-cols-1 md:grid-cols-5 gap-3">

              <input
                value={search}
                onChange={
                  e =>
                    setSearch(
                      e.target.value
                    )
                }
                placeholder="Search name / certificate / email"
                className="border rounded px-3 py-2"
              />


              <select
                value={groupFilter}
                onChange={
                  e =>
                    setGroupFilter(
                      e.target.value
                    )
                }
                className="border rounded px-3 py-2"
              >
                <option value="all">
                  All Groups
                </option>

                {groups.map(
                  group => (
                    <option
                      key={group}
                      value={group}
                    >
                      {group}
                    </option>
                  )
                )}
              </select>


              <select
                value={
                  generationFilter
                }
                onChange={
                  e =>
                    setGenerationFilter(
                      e.target.value
                    )
                }
                className="border rounded px-3 py-2"
              >
                <option value="all">
                  All Generation
                </option>

                <option value="Pending">
                  Pending
                </option>

                <option value="Generated">
                  Generated
                </option>

                <option value="Failed">
                  Failed
                </option>
              </select>


              <select
                value={emailFilter}
                onChange={
                  e =>
                    setEmailFilter(
                      e.target.value
                    )
                }
                className="border rounded px-3 py-2"
              >
                <option value="all">
                  All Email
                </option>

                <option value="Not Sent">
                  Not Sent
                </option>

                <option value="Sent">
                  Sent
                </option>

                <option value="Failed">
                  Failed
                </option>
              </select>


              <select
                value={rowFilter}
                onChange={
                  e =>
                    setRowFilter(
                      e.target.value
                    )
                }
                className="border rounded px-3 py-2"
              >
                <option value="all">
                  All Records
                </option>

                <option value="Valid">
                  Valid
                </option>

                <option value="Invalid">
                  Invalid
                </option>
              </select>

            </div>


            <div className="flex flex-wrap gap-2 mt-4">

              <button
                onClick={
                  selectAllVisible
                }
                className="px-3 py-2 bg-blue-100 text-blue-700 rounded"
              >
                Select All Visible
              </button>

              <button
                onClick={
                  clearSelection
                }
                className="px-3 py-2 bg-gray-100 rounded"
              >
                Clear Selection
              </button>

              <span className="px-3 py-2 text-sm">
                Showing {filtered.length} of {certificates.length}
                {' | '}
                Selected {selectedIds.length}
              </span>

            </div>
          </div>


          <div className="bg-white rounded-lg shadow overflow-x-auto">

            <table className="min-w-full border-collapse">

              <thead className="bg-gray-100">
                <tr>
                  <th className="border p-2">
                    Select
                  </th>

                  <th className="border p-2">
                    Certificate No
                  </th>

                  <th className="border p-2">
                    Participant
                  </th>

                  <th className="border p-2">
                    Group
                  </th>

                  <th className="border p-2">
                    Recipient Email
                  </th>

                  <th className="border p-2">
                    Validation
                  </th>

                  <th className="border p-2">
                    PDF
                  </th>

                  <th className="border p-2">
                    Email
                  </th>

                  <th className="border p-2">
                    View
                  </th>
                </tr>
              </thead>

              <tbody>

                {filtered.map(
                  item => (

                    <tr
                      key={item.id}
                    >

                      <td className="border p-2 text-center">
                        <input
                          type="checkbox"
                          disabled={
                            item.row_status !==
                            'Valid'
                          }
                          checked={
                            selectedIds.includes(
                              item.id
                            )
                          }
                          onChange={() =>
                            toggleSelection(
                              item.id
                            )
                          }
                        />
                      </td>

                      <td className="border p-2">
                        {item.certificate_no}
                      </td>

                      <td className="border p-2">
                        {item.participant_name}
                      </td>

                      <td className="border p-2">
                        {item.group_name || '-'}
                      </td>

                      <td className="border p-2">
                        {item.recipient_email || (
                          <span className="text-red-600">
                            Missing
                          </span>
                        )}
                      </td>

                      <td className="border p-2">
                        {item.row_status}

                        {item.validation_error && (
                          <div className="text-xs text-red-600">
                            {item.validation_error}
                          </div>
                        )}
                      </td>

                      <td className="border p-2">
                        {item.generation_status}
                      </td>

                      <td className="border p-2">
                        {item.email_status}
                      </td>

                      <td className="border p-2">

                        {item.generation_status ===
                          'Generated' && (

                          <button
                            onClick={() =>
                              certificateApi
                                .downloadCertificate(
                                  item.id
                                )
                            }
                            className="text-blue-600 underline"
                          >
                            View PDF
                          </button>

                        )}

                      </td>

                    </tr>

                  )
                )}

              </tbody>
            </table>

          </div>


          <div className="bg-white rounded-lg shadow p-4">

            <h2 className="font-semibold mb-3">
              Step 1 - PDF Generation
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">

              {[
                [
                  'INDIVIDUAL',
                  'Individual PDFs',
                  'One PDF per participant'
                ],

                [
                  'SELECTED',
                  'Selected → One PDF',
                  'Selected certificates page-by-page'
                ],

                [
                  'GROUP',
                  'Group-wise PDFs',
                  'One multi-page PDF per group'
                ],

                [
                  'BATCH',
                  'Full Batch PDF',
                  'All valid certificates in one PDF'
                ]
              ].map(
                option => (

                  <label
                    key={option[0]}
                    className={`border rounded-lg p-3 cursor-pointer ${
                      pdfMode ===
                      option[0]
                        ? 'border-blue-500 bg-blue-50'
                        : ''
                    }`}
                  >
                    <input
                      type="radio"
                      value={
                        option[0]
                      }
                      checked={
                        pdfMode ===
                        option[0]
                      }
                      onChange={
                        e =>
                          setPdfMode(
                            e.target.value
                          )
                      }
                    />

                    <div className="font-medium mt-2">
                      {option[1]}
                    </div>

                    <div className="text-xs text-gray-500">
                      {option[2]}
                    </div>
                  </label>

                )
              )}

            </div>

            <button
              onClick={generate}
              disabled={loading}
              className="mt-4 bg-blue-600 text-white px-5 py-2 rounded"
            >
              {loading
                ? 'Processing...'
                : 'Generate Certificates'}
            </button>

          </div>


          <div className="bg-white rounded-lg shadow p-4">

            <h2 className="font-semibold mb-3">
              Generated Combined PDFs
            </h2>

            <div className="space-y-2">

              {outputs.map(
                output => (

                  <div
                    key={output.id}
                    className="flex items-center justify-between border rounded p-3"
                  >
                    <div>
                      <strong>
                        {output.output_name}
                      </strong>

                      <div className="text-xs text-gray-500">
                        {output.output_type}
                        {' | '}
                        {output.page_count} page(s)
                        {output.group_name
                          ? ` | ${output.group_name}`
                          : ''}
                      </div>
                    </div>

                    <button
                      onClick={() =>
                        certificateApi
                          .downloadOutput(
                            output.id
                          )
                      }
                      className="text-blue-600 underline"
                    >
                      View / Download
                    </button>
                  </div>

                )
              )}

            </div>
          </div>


          <div className="bg-white rounded-lg shadow p-4">

            <h2 className="font-semibold mb-3">
              Step 2 - Email Delivery
            </h2>

            <select
              value={emailMode}
              onChange={
                e =>
                  setEmailMode(
                    e.target.value
                  )
              }
              className="border rounded px-3 py-2 w-full md:w-96"
            >
              <option value="INDIVIDUAL_EMAIL">
                Individual Email - one certificate per recipient
              </option>

              <option value="ONE_EMAIL_MULTI_ATTACHMENTS">
                One Email - multiple individual PDF attachments
              </option>

              <option value="ONE_EMAIL_COMBINED">
                One Email - one combined PDF
              </option>

              <option value="GROUP_EMAIL">
                Group-wise Email
              </option>
            </select>


            {[
              'ONE_EMAIL_MULTI_ATTACHMENTS',
              'ONE_EMAIL_COMBINED'
            ].includes(
              emailMode
            ) && (

              <input
                value={
                  commonRecipient
                }
                onChange={
                  e =>
                    setCommonRecipient(
                      e.target.value
                    )
                }
                placeholder="Recipient Email"
                className="block border rounded px-3 py-2 mt-3 w-full md:w-96"
              />

            )}


            <input
              value={subject}
              onChange={
                e =>
                  setSubject(
                    e.target.value
                  )
              }
              placeholder="Email Subject"
              className="block border rounded px-3 py-2 mt-3 w-full"
            />


            <textarea
              value={emailBody}
              onChange={
                e =>
                  setEmailBody(
                    e.target.value
                  )
              }
              rows="4"
              className="block border rounded px-3 py-2 mt-3 w-full"
              placeholder="Email body"
            />


            <button
              onClick={
                sendEmail
              }
              disabled={loading}
              className="mt-4 bg-green-600 text-white px-5 py-2 rounded"
            >
              Send Certificate Email
            </button>

          </div>

        </>
      )}

    </div>
  );
};

export default CertificateManagement;